import { type FC, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControlLabel,
  LinearProgress,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useAbility } from "@/partner/shared/lib";
import {
  useCurrentPlatformQuery,
  useUpdateCurrentPlatformMutation,
} from "@/partner/entities/platforms";
import {
  marketingErrorKey,
  useMarketingQuotaUsageQuery,
} from "@/partner/entities/marketing";
import type { MarketingConsentModeType } from "@/types/graphql/partner.generated";
import {
  type MarketingConsentModeValue,
  quotaPercent,
  validateMarketingSettings,
} from "../model/settingsForm";

/**
 * Настройки рассылок (спека marketing-automations, фаза 1, Task 9): режим
 * согласия, потолок частоты и квота писем в месяц. Блок страницы настроек
 * платформы (PlatformSettings.tsx) при фичефлаге MARKETING_EMAILS; сохраняет
 * своими полями через updateCurrentPlatform (по образцу
 * LiveReactionsSettingsCard). Кнопка «Сохранить» недоступна без права
 * `update` на `Platform` (страницу настроек и так гейтит это право).
 */
export const MarketingSettingsCard: FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const ability = useAbility();
  const canUpdate = ability.can("update", "Platform");

  const { data } = useCurrentPlatformQuery();
  const platform = data?.currentPlatform;
  const { data: usageData } = useMarketingQuotaUsageQuery();
  const usage = usageData?.marketingQuotaUsage;

  // Ленивый инициализатор, а не просто дефолт + эффект: если платформа уже
  // в кеше Apollo к моменту монтирования (cache-first), первый рендер сразу
  // показывает сохранённые значения, а не на секунду мигает дефолтом
  // "3/7/OPT_IN" до срабатывания useEffect ниже. OPT_IN это и дефолт
  // бэкенда (MARKETING_DEFAULTS.consentMode): письма только зрителям с
  // галочкой согласия.
  const [consentMode, setConsentMode] = useState<MarketingConsentModeValue>(
    () =>
      (platform?.marketingSettings
        .consentMode as unknown as MarketingConsentModeValue) ?? "OPT_IN"
  );
  const [frequencyCapCount, setFrequencyCapCount] = useState(
    () => platform?.marketingSettings.frequencyCapCount ?? 3
  );
  const [frequencyCapDays, setFrequencyCapDays] = useState(
    () => platform?.marketingSettings.frequencyCapDays ?? 7
  );

  useEffect(() => {
    if (!platform) return;
    setConsentMode(
      platform.marketingSettings
        .consentMode as unknown as MarketingConsentModeValue
    );
    setFrequencyCapCount(platform.marketingSettings.frequencyCapCount);
    setFrequencyCapDays(platform.marketingSettings.frequencyCapDays);
  }, [platform]);

  const [updateCurrentPlatform, { loading }] = useUpdateCurrentPlatformMutation(
    {
      refetchQueries: ["CurrentPlatform"],
      onCompleted: () =>
        enqueueSnackbar(t("marketingSettings.saved"), {
          variant: "success",
        }),
      onError: (error) =>
        enqueueSnackbar(t(marketingErrorKey(error)), { variant: "error" }),
    }
  );

  const errors = validateMarketingSettings({
    consentMode,
    frequencyCapCount,
    frequencyCapDays,
  });
  const hasErrors = Object.keys(errors).length > 0;
  // Как у формы настроек выше: «Сохранить» активна только при изменениях.
  const saved = platform?.marketingSettings;
  const isDirty =
    !!saved &&
    ((saved.consentMode as unknown as MarketingConsentModeValue) !==
      consentMode ||
      saved.frequencyCapCount !== frequencyCapCount ||
      saved.frequencyCapDays !== frequencyCapDays);

  const handleSave = () => {
    if (hasErrors || !canUpdate) return;
    void updateCurrentPlatform({
      variables: {
        input: {
          marketingSettings: {
            consentMode: consentMode as unknown as MarketingConsentModeType,
            frequencyCapCount,
            frequencyCapDays,
          },
        },
      },
    });
  };

  const sent = usage?.sent ?? 0;
  const quota = usage?.quota ?? 0;
  const quotaReached = quota > 0 && sent >= quota;

  return (
    <Card sx={{ mt: 2 }}>
      <CardHeader
        title={t("marketingSettings.title")}
        subheader={t("marketingSettings.subtitle")}
        sx={{ px: 2, pt: 2, pb: 0 }}
      />
      <CardContent sx={{ px: 2, pb: 2, pt: 2 }}>
        <Stack spacing={3}>
          <RadioGroup
            value={consentMode}
            onChange={(event) =>
              setConsentMode(event.target.value as MarketingConsentModeValue)
            }
          >
            <FormControlLabel
              value="OPT_IN"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body2">
                    {t("marketingSettings.consent.optIn")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("marketingSettings.consent.optInHint")}
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="OPT_OUT"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body2">
                    {t("marketingSettings.consent.optOut")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("marketingSettings.consent.optOutHint")}
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
          {consentMode === "OPT_OUT" && (
            <Alert severity="warning">
              {t("marketingSettings.consent.optOutWarning")}
            </Alert>
          )}
          {saved?.consentModeChangedAt && (
            <Typography variant="caption" color="text.secondary">
              {t("marketingSettings.consent.changedAt", {
                value: new Date(saved.consentModeChangedAt),
              })}
            </Typography>
          )}

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t("marketingSettings.capHint")}
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                type="number"
                label={t("marketingSettings.capCount")}
                size="small"
                value={frequencyCapCount}
                onChange={(event) =>
                  setFrequencyCapCount(Number(event.target.value))
                }
                error={Boolean(errors.frequencyCapCount)}
                helperText={
                  errors.frequencyCapCount ? t(errors.frequencyCapCount) : " "
                }
              />
              <TextField
                type="number"
                label={t("marketingSettings.capDays")}
                size="small"
                value={frequencyCapDays}
                onChange={(event) =>
                  setFrequencyCapDays(Number(event.target.value))
                }
                error={Boolean(errors.frequencyCapDays)}
                helperText={
                  errors.frequencyCapDays ? t(errors.frequencyCapDays) : " "
                }
              />
            </Stack>
          </Box>

          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t("marketingSettings.quota", { sent, quota })}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={quotaPercent({ sent, quota })}
            />
            {quotaReached && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {t("marketingSettings.quotaReached")}
              </Alert>
            )}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Tooltip
              title={canUpdate ? "" : t("marketingSettings.saveDisabledHint")}
            >
              <span>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={loading || hasErrors || !isDirty || !canUpdate}
                >
                  {t("marketingSettings.save")}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};
